'use client'

import { ListItemButton, ListItemText } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { usePathname, useRouter } from 'next/navigation'

import {
  AICLAIMS_WELCOME_PATH,
  isAiClaimsPortalPath,
} from '../../config/aiclaims-routes'
import useAuth from '../../hooks/useAuth'
import { navigateToBrandPortal } from '../../lib/crossDomainAuth'
import { effectiveBrandAccess } from '../../lib/pageAccess'
import { UsersApi } from '../../services'

type Props = { isCollapsed: boolean }

/**
 * Wysehub sidebar: switch to Ai Claims when user has both brands.
 * Ai Claims shell renders its own switch back to Wysehub.
 */
export default function BrandSwitcher({ isCollapsed }: Props) {
  const { user } = useAuth()
  const router = useRouter()
  const pathname = usePathname() || ''
  const queryClient = useQueryClient()

  if (effectiveBrandAccess(user) !== 'both') return null
  if (isAiClaimsPortalPath(pathname)) return null

  const goAiClaims = async () => {
    navigateToBrandPortal('aiclaims', AICLAIMS_WELCOME_PATH, async () => {
      await new UsersApi().usersSetActiveBrandMe({
        requestBody: { activeBrand: 'aiclaims' },
      })
      await queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      router.push(AICLAIMS_WELCOME_PATH)
    })
  }

  if (isCollapsed) return null

  return (
    <ListItemButton
      onClick={() => void goAiClaims()}
      // biome-ignore lint/plugin: compact vertical inset for the sidebar switcher row
      sx={{ py: 0.5 }}
    >
      <ListItemText primary="Switch to Ai Claims" />
    </ListItemButton>
  )
}
